<template>
  <div>
    <v-data-table
      v-model="selected"
      class="mb-5 pb-5"
      :headers="tableHeaders"
      :items="items"
      :item-key="itemKey"
      :search="search"
      :items-per-page="visibleItemsPerPage"
      :show-select="massActions.length > 0"
      :class="getCssClasses()"
      :page.sync="page"
      multi-sort
      hide-default-footer
      @page-count="pageCount = $event"
      @click:row="rowClick"
    >
      <template v-slot:top>
        <v-toolbar flat class="pl-0">
          <v-row>
            <v-col cols="8">
              <v-text-field
                v-model="search"
                :label="searchFieldLabel"
                class="ml-auto"
                append-icon="mdi-magnify"
              />
            </v-col>
            <v-col
              v-if="showCrudActions && $vuetify.breakpoint.lgAndUp"
              cols="12"
              sm="auto"
              xs="auto"
              lg="auto"
              class="ml-md-auto d-flex justify-center align-center"
            >
              <v-btn
                fab
                x-small
                color="primary"
                @click="$emit('add-data-action')"
              >
                <v-icon>
                  mdi-plus
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-toolbar>
      </template>

      <template v-slot:item.actions="{ on, item }">
        <template v-if="showCrudActions">
          <v-btn icon v-on="on" @click="$emit('row-edit-action', item)">
            <v-icon :size="iconSize">
              mdi-pencil
            </v-icon>
          </v-btn>
          <v-btn icon v-on="on" @click="$emit('row-delete-action', item)">
            <v-icon :size="iconSize">
              mdi-delete
            </v-icon>
          </v-btn>
        </template>
        <v-menu
          v-if="showRowActions"
          transition="slide-y-transition"
          left
          offset-y
        >
          <template v-slot:activator="{ on: menu, attrs }">
            <v-btn
              outlined
              v-bind="attrs"
              class="row-menu-action-button"
              v-on="menu"
            >
              <v-icon :size="iconSize">
                mdi-menu-down
              </v-icon>
            </v-btn>
          </template>

          <slot name="item.dropdown.actions" :item="item">
            <v-list>
              <v-list-item
                v-for="action in rowActions"
                :key="action.value"
                link
                @click="rowActionClick(action, item)"
                v-text="action.text"
              >
              </v-list-item>
            </v-list>
          </slot>
        </v-menu>
      </template>

      <template v-slot:footer>
        <mass-actions-row
          :selected="selected"
          :actions="massActions"
          :active-action="activeAction"
          :icon-size="iconSize"
          :show-crud-actions="showCrudActions"
          :mass-action-selected-label="massActionSelectedLabel"
          :mass-action-select-label="massActionSelectLabel"
          @activate-action="activateAction"
          @clear-action="clearAction"
          @reset-selection="resetSelection"
          @mass-execute-action="executeAction"
          @mass-delete-action="massDeleteAction"
        />
      </template>
    </v-data-table>

    <ribbon />

    <v-row justify="center" class="mt-0">
      <v-col
        cols="8"
        sm="12"
        class="d-inline-flex justify-center aling-center pagination-col"
      >
        <button
          type="button"
          class="pagination-button pagination-prev v-pagination__item align-self-center"
          @click="page = 1"
          v-text="'1'"
        />
        <v-pagination
          v-model="page"
          :length="pageCount"
          total-visible="6"
          color="btn-primary"
          class="pagination align-self-center"
        />
        <button
          type="button"
          class="pagination-button pagination-next v-pagination__item align-self-center"
          @click="page = pageCount"
          v-text="pageCount"
        />
        <v-select
          v-model="visibleItemsPerPage"
          class="pagination-select"
          hide-details
          outlined
          height="36"
          append-icon="mdi-menu-swap"
          :items="getPaginationSelectItems()"
          :menu-props="getPaginationSelectMenuProps()"
        />
        <span
          v-t="'Components.DataTable.Pagination.Data'"
          cols="auto"
          class="pagination-select-label d-none d-md-block"
        />
      </v-col>
    </v-row>

    <ribbon />

    <v-btn
      v-if="showCrudActions && $vuetify.breakpoint.mdAndDown"
      fab
      dark
      fixed
      bottom
      right
      color="primary"
      @click="$emit('add-data-action')"
    >
      <v-icon>
        mdi-plus
      </v-icon>
    </v-btn>
  </div>
</template>

<script>
import MassActionsRow from "+/components/table/DataTableMassActionsRow";
import Ribbon from "+/components/ribbon/Ribbon";
export default {
  name: "PDataTable",
  components: {
    "mass-actions-row": MassActionsRow,
    ribbon: Ribbon
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    headers: {
      type: Array,
      default: () => []
    },
    actionHeader: {
      type: Object,
      default: () => ({
        text: "",
        value: "actions",
        sortable: false,
        align: "end",
        width: "1%",
        class: "action-col"
      })
    },
    itemKey: {
      type: String,
      default: "id"
    },
    showRowActions: {
      type: Boolean,
      default: false
    },
    showCrudActions: {
      type: Boolean,
      default: false
    },
    massActions: {
      type: Array,
      default: () => []
    },
    rowActions: {
      type: Array,
      default: () => []
    },
    itemsPerPage: {
      type: Number,
      default: 10
    },
    iconSize: {
      type: Number,
      default: 18
    },
    cssClass: {
      type: String,
      default: ""
    },
    searchFieldLabel: {
      type: String,
      default() {
        return this.$t("Components.DataTable.Search");
      }
    },
    massActionSelectedLabel: {
      type: String,
      default() {
        return this.$t("Components.DataTable.MassActionSelectedLabel");
      }
    },
    massActionSelectLabel: {
      type: String,
      default() {
        return this.$t("Components.DataTable.MassActionSelectActionLabel");
      }
    },
    searchValue: {
      type: String,
      default: ""
    },
    showPointer: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      search: this.searchValue,
      selected: [],
      activeAction: undefined,
      tableHeaders: [],
      page: 1,
      pageCount: 0,
      visibleItemsPerPage: this.itemsPerPage
    };
  },
  created() {
    if (this.showRowActions || this.showCrudActions) {
      this.tableHeaders = [...this.headers, ...[this.actionHeader]];
    } else {
      this.tableHeaders = this.headers;
    }
  },
  methods: {
    rowClick(item) {
      this.$emit("row-click", item);
    },
    getPaginationSelectItems() {
      const itemsPerPageArray = [10, 25, 50, 75, 100, 200, 500];
      return itemsPerPageArray.map(item => ({ text: item, value: item }));
    },
    rowActionClick(action, item) {
      this.$emit("row-action-click", {
        action,
        item
      });
    },
    executeAction() {
      this.$emit("mass-execute-action", {
        action: this.activeAction,
        selected: this.selected
      });
    },
    massDeleteAction() {
      this.$emit("mass-delete-action", this.selected);
    },
    activateAction(action) {
      this.activeAction = action;
    },
    resetSelection() {
      this.selected = [];
      this.activeAction = undefined;
    },
    clearAction() {
      this.activeAction = undefined;
    },
    getCssClasses() {
      return this.showPointer
        ? `${this.cssClass} clickable-row`
        : this.cssClass;
    },
    getPaginationSelectMenuProps() {
      return {
        "offset-y": true,
        "close-on-content-click": true
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../_variables.scss";
::v-deep {
  .v-data-table {
    &.clickable-row {
      tr td:hover {
        cursor: pointer;
      }
    }
  }

  .v-text-field[data-component-name="v-text-field"] {
    .v-input__append-inner {
      visibility: visible;
    }
  }

  .v-pagination__item,
  .v-pagination__navigation {
    box-shadow: none;
    text-align: center;
    background: $pagination-background;
    padding: 0.5rem 0.75rem;
    font-weight: 400;
    font-size: $pagination-font-size;
    line-height: 1.25;
    border: 1px solid $pagination-border-color;
    margin: 0 !important;
    border-radius: 0;
    &:hover {
      background-color: $pagination-hover-background-color;
    }
    .v-icon {
      font-size: $pagination-font-size;
    }
  }
  .pagination-select {
    max-width: 5.2rem;
    margin-top: 0.13rem;
    margin-left: 1rem;
    font-size: $pagination-font-size;
    .v-input__slot {
      min-height: auto !important;
    }
    &.v-select--is-menu-active .v-input__icon--append .v-icon {
      transform: none;
    }

    .v-select__selection--comma {
      margin-right: 0px;
    }

    .v-input__append-inner {
      margin-top: 6px !important;
      padding-left: 0px;
      font-size: $pagination-font-size;
      .v-icon::before {
        color: $pagination-select-icon-color;
      }
    }
  }
  .v-pagination__navigation {
    padding: 16px 0 16px 0;
  }
}
.pagination-button {
  height: 34px;
  width: auto;
  cursor: pointer;
  &.pagination-prev {
    border-radius: $pagination-border-radius 0 0 $pagination-border-radius;
  }
  &.pagination-next {
    border-radius: 0 $pagination-border-radius $pagination-border-radius 0;
  }
}
.pagination-col {
  padding: 0 !important;
  height: 2.4rem;
  .pagination-select-label {
    margin: 9px 0 0 6px;
    font-size: $pagination-font-size;
  }
}
</style>
